/**
 * Generate a quadratic bezier curve between two points.
 * Curve bows perpendicular to the line connecting start and end.
 *
 * @param {number[]} start - [lng, lat]
 * @param {number[]} end - [lng, lat]
 * @param {number} curveFactor - How much the curve bows (0 = straight, 0.2 = moderate)
 * @param {number} segments - Number of line segments (more = smoother)
 * @returns {number[][]} Array of [lng, lat] coordinate pairs
 */
export function calculateBezierCurve(start, end, curveFactor, segments) {
  const midX = (start[0] + end[0]) / 2
  const midY = (start[1] + end[1]) / 2

  // Perpendicular offset for the control point
  const dx = end[0] - start[0]
  const dy = end[1] - start[1]
  const controlX = midX - dy * curveFactor
  const controlY = midY + dx * curveFactor

  const points = []
  for (let i = 0; i <= segments; i++) {
    const t = i / segments
    const oneMinusT = 1 - t

    // Quadratic bezier: B(t) = (1-t)^2 * P0 + 2(1-t)t * P1 + t^2 * P2
    const x = oneMinusT * oneMinusT * start[0] + 2 * oneMinusT * t * controlX + t * t * end[0]
    const y = oneMinusT * oneMinusT * start[1] + 2 * oneMinusT * t * controlY + t * t * end[1]
    points.push([x, y])
  }

  return points
}

/**
 * Normalize a volume value to the 0-1 range.
 *
 * @param {number} volume - The volume to normalize
 * @param {number} maxVolume - The maximum volume in the dataset
 * @returns {number} Normalized value between 0 and 1
 */
export function normalizeVolume(volume, maxVolume) {
  if (maxVolume <= 0) return 0
  return Math.min(1, Math.max(0, volume / maxVolume))
}

/**
 * Get the display volume for a route at a specific day/hour.
 * Returns totalVolume if no day specified, day sum if no hour, or specific hour value.
 *
 * @param {Object} route - Route object with volume[dow][hour] and totalVolume
 * @param {string|null} day - Day of week ("1"-"7") or null for semester total
 * @param {number|null} hour - Hour (0-23) or null for full day
 * @returns {number} Volume value
 */
export function getRouteVolumeForTime(route, day, hour) {
  if (day == null) return route.totalVolume

  const dayData = route.volume[String(day)]
  if (!dayData) return 0

  if (hour == null) {
    return dayData.reduce((sum, h) => sum + h, 0)
  }

  return dayData[hour] ?? 0
}

/**
 * Filter routes by percentile threshold, optionally scoped to a specific building.
 *
 * In semester mode (no displayVolumeFn): uses pre-baked percentile / fromBuildingPercentile / toBuildingPercentile.
 * In hourly/day mode (displayVolumeFn provided): computes runtime percentiles from temporal volumes.
 *
 * @param {Array} routes - Route objects with percentile, fromBuildingPercentile, toBuildingPercentile
 * @param {number} threshold - Minimum percentile (0-95). 0 means show all.
 * @param {string|null} buildingId - If set, only show routes involving this building
 * @param {Function|null} displayVolumeFn - If provided, compute runtime percentiles using this
 *   function (called as displayVolumeFn(route)) instead of pre-baked route.percentile.
 * @returns {Array} Filtered routes
 */
export function filterRoutesByThreshold(routes, threshold, buildingId = null, displayVolumeFn = null) {
  if (threshold === 0 && !buildingId) return routes

  // Narrow to routes involving the selected building
  let candidateRoutes = routes
  if (buildingId) {
    candidateRoutes = routes.filter(
      r => r.origin === buildingId || r.destination === buildingId
    )
  }

  if (threshold === 0) return candidateRoutes

  // Hourly/day mode: compute runtime percentiles
  if (displayVolumeFn) {
    const withVolumes = candidateRoutes
      .map(route => ({ route, displayVolume: displayVolumeFn(route) }))
      .sort((a, b) => a.displayVolume - b.displayVolume)

    const count = withVolumes.length
    return withVolumes
      .filter((_, i) => {
        const percentile = count > 1 ? Math.round((i / (count - 1)) * 100) : 100
        return percentile >= threshold
      })
      .map(item => item.route)
  }

  // Semester mode: use pre-baked percentiles
  return candidateRoutes.filter(route => {
    if (buildingId) {
      const isOrigin = route.origin === buildingId
      return isOrigin
        ? route.fromBuildingPercentile >= threshold
        : route.toBuildingPercentile >= threshold
    }
    return route.percentile >= threshold
  })
}

export const FLOW_CONSTANTS = {
  COLOR: "#E91E9E",
  OPACITY: 0.8,
  DASH_PATTERN: [4, 4],
  MIN_WIDTH: 0.5,
  MAX_WIDTH: 3,
  CURVE_FACTOR: 0.2,
  CURVE_SEGMENTS: 20,
  ANIMATION_FPS: 12,
}

/**
 * Build a display volume function for temporal filtering modes.
 * Returns null in semester mode (all 7 days, no hour = use pre-baked percentiles).
 * Returns a function that sums volumes across selected days/hour for temporal modes.
 *
 * @param {Array|null} days - Selected days of week (e.g., [1,2,3,4,5])
 * @param {number|null} hour - Selected hour (0-23) or null for full day
 * @returns {Function|null} Volume function or null
 */
export function buildDisplayVolumeFn(days, hour) {
  const isAllDays = !days?.length || days.length >= 7
  const isSemesterMode = isAllDays && hour == null
  if (isSemesterMode) return null

  const selectedHour = hour != null ? Number(hour) : null
  const daysToSum = isAllDays
    ? ["1", "2", "3", "4", "5", "6", "7"]
    : days.map(String)

  return route => {
    let total = 0
    for (const day of daysToSum) {
      total += getRouteVolumeForTime(route, day, selectedHour)
    }
    return total
  }
}

const EMPTY_GEOJSON = { type: "FeatureCollection", features: [] }

/**
 * Build filtered flow GeoJSON from campus flow data and current filter state.
 * Extracted as a pure function for testability.
 *
 * @param {Object|null} campusFlowData - Raw campus flow data ({ routes, buildings, maxVolume })
 * @param {boolean} campusFlow - Whether campus flow toggle is on
 * @param {number} flowThreshold - Percentile threshold (0-95)
 * @param {string|null} flowBuilding - Selected building ID or null
 * @param {Array|null} days - Selected days of week
 * @param {number|null} hour - Selected hour
 * @returns {Object} GeoJSON FeatureCollection
 */
export function buildFilteredFlowGeoJSON(campusFlowData, campusFlow, flowThreshold, flowBuilding, days, hour) {
  if (!campusFlow || !campusFlowData?.routes?.length) return EMPTY_GEOJSON

  const displayVolumeFn = buildDisplayVolumeFn(days, hour)
  const filteredRoutes = filterRoutesByThreshold(
    campusFlowData.routes,
    flowThreshold,
    flowBuilding,
    displayVolumeFn
  )

  return buildFlowGeoJSON(
    filteredRoutes,
    campusFlowData.buildings,
    campusFlowData.maxVolume || 1,
    displayVolumeFn
  )
}

/**
 * Build a GeoJSON FeatureCollection from route data and building centroids.
 * Each route becomes a curved LineString feature.
 *
 * @param {Array} routes - Route objects with id, origin, destination, totalVolume, percentile, etc.
 * @param {Object} buildings - Map of building ID to { centroid: [lng, lat] }
 * @param {number} maxVolume - Maximum volume in the dataset (for normalization in semester mode)
 * @param {Function|null} displayVolumeFn - If provided, use this to compute per-route volume
 *   for normalization (for day/hour temporal modes). Falls back to route.totalVolume when null.
 * @returns {Object} GeoJSON FeatureCollection
 */
export function buildFlowGeoJSON(routes, buildings, maxVolume, displayVolumeFn = null) {
  // When using temporal volumes, derive maxVolume from the filtered routes
  let effectiveMaxVolume = maxVolume
  if (displayVolumeFn) {
    effectiveMaxVolume = routes.reduce(
      (max, route) => Math.max(max, displayVolumeFn(route)), 0
    ) || 1
  }

  const features = []

  for (const route of routes) {
    const originBuilding = buildings[route.origin]
    const destBuilding = buildings[route.destination]

    if (!originBuilding?.centroid || !destBuilding?.centroid) continue

    const curveFactor = FLOW_CONSTANTS.CURVE_FACTOR

    const coordinates = calculateBezierCurve(
      originBuilding.centroid,
      destBuilding.centroid,
      curveFactor,
      FLOW_CONSTANTS.CURVE_SEGMENTS
    )

    const volume = displayVolumeFn ? displayVolumeFn(route) : route.totalVolume
    const normalizedVol = normalizeVolume(volume, effectiveMaxVolume)

    features.push({
      type: "Feature",
      id: route.id,
      geometry: {
        type: "LineString",
        coordinates,
      },
      properties: {
        id: route.id,
        origin: route.origin,
        destination: route.destination,
        originName: originBuilding.name || "",
        destinationName: destBuilding.name || "",
        volume,
        normalizedVolume: normalizedVol,
        percentile: route.percentile,
        fromBuildingPercentile: route.fromBuildingPercentile,
        toBuildingPercentile: route.toBuildingPercentile,
      },
    })
  }

  return {
    type: "FeatureCollection",
    features,
  }
}
